import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useChatProfileStore } from 'components/AskDelaChat/ChatProfileStore'
import {
    elementToEditorData,
    extractSuggestions,
    getUnresolvedChanges,
    processContent,
} from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import {
    useCKEditorToggle,
    useContentItemViewNode,
    usePromoteDemote,
} from 'components/WorkspaceEditor/hooks'
import {
    useContentMutationService,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import {
    nullOrUndefinedResolver,
    withDebounce,
} from 'components/WorkspaceEditor/utils'
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { batchUpdate } from 'shared/utils/workspaceUtils'
import {
    useShallowSelector,
    useTextContentElementToAdd,
} from '../../../Workspace2Store'
import type {
    ClassicEditorHandler,
    ClassicEditorOnReadyHandler,
} from '../../ContentEditor'
import { useTreeMutations } from '../../TrackChangesPanel/hooks/TreeMutations/useTreeMutations'
import { usePlainTextContentMutation } from './usePlainTextContentMutation'

/**
 * Defines the structure for CKEditor event handlers used in the plain text controller.
 */
interface PlainTextHandlers {
    /** Handler triggered when the CKEditor instance is ready. Used to store the editor reference for potential rollbacks. */
    onReady: ClassicEditorOnReadyHandler
    /** Handler triggered when the CKEditor instance loses focus. Handles saving changes via optimistic updates. */
    onBlur: ClassicEditorHandler
    /** Debounced handler triggered when the CKEditor content changes. Calculates and stores unsaved changes. */
    onChange: ClassicEditorHandler
    /** Handler triggered when the CKEditor instance gains focus. Handles setting the editor to active state. */
    onFocusCapture: () => void
    /** Handler triggered when a key is pressed while the CKEditor instance has focus. Handles promotion and demotion. */
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
}

/**
 * Defines the structure for the changed elements in the CKEditor content.
 * This includes the change type, ID, user ID, and text of the element.
 */
export interface ChangedElements {
    changeType:
        | 'ADDED'
        | 'MODIFIED'
        | 'DELETED'
        | 'ACCEPTED'
        | 'REPLACED'
        | null
    id: string
    authoredUserId: string
    text: string
}

/** Props for the useContentEditorFocus hook. */
interface UseContentEditorFocusProps {
    /** The ID of the content item to be focused. */
    contentItemId?: string
    /** The optional handler to activate CKEditor. */
    activate?: () => void
}

/**
 * Custom hook that handles setting focus on `editingTextContentElement` by matching its id.
 *
 * @param {UseContentEditorFocusProps} props - Props for the hook.
 * @param {string} props.contentItemId - The ID of the content item to be focused.
 * @param {object} props.activate - The optional handler to activate CKEditor.
 * @returns The CKEditor (or type of T) ref to focus with.
 */
export const useContentEditorFocus = <T extends { focus: () => void }>({
    contentItemId,
    activate,
}: UseContentEditorFocusProps) => {
    const editorFocusRef: MutableRefObject<T | null> = useRef(null)
    const editingTextContentElement = useTextContentElementToAdd()
    const ckeToggle = useCKEditorToggle(contentItemId)

    //deFocus the editor when there add operation started
    const unFocusEditor = useCallback(() => {
        if (editorFocusRef?.current) {
            editorFocusRef.current = null
            ckeToggle.deactivate()
        }
    }, [editorFocusRef])

    useEffect(() => {
        if (editingTextContentElement?.id === contentItemId) {
            activate?.()

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    editorFocusRef.current?.focus?.()
                })
            })
        }
    }, [editingTextContentElement, activate, contentItemId])

    return { editorFocusRef, unFocusEditor }
}

/**
 * Custom hook that handles parsing and manipulating keyed text content.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item data, potentially incomplete.
 * @param {boolean} [isContentTeamFromStudioPage] - Whether the current content is from SMM/OM with special permissions.
 * @returns An object containing parsed text and handlers for managing keyed content.
 */
export const useKeyedText = (
    contentItem: Partial<TextContentElement>,
    isContentTeamFromStudioPage?: boolean
) => {
    const text = contentItem?.text?.trim() || ''
    const hasColonInText = text.includes(':')
    const isKeyedText = hasColonInText && !isContentTeamFromStudioPage

    const { keyedText, keyedTextValue } = useMemo(() => {
        if (!isKeyedText) {
            return { keyedText: ': ', keyedTextValue: undefined }
        }

        // extract key and value parts
        const keyPart = text.split(':')?.[0] || ''
        const valuePart = text.substring(text.indexOf(':') + 1).trim()

        return {
            keyedText: `${keyPart}: `,
            keyedTextValue: valuePart,
        }
    }, [text, isKeyedText])

    // remove label prefix for editor display
    const removeKeyedText = useCallback(
        (textToModify: string) =>
            isKeyedText ? textToModify.replace(keyedText, '') : textToModify,
        [isKeyedText, keyedText]
    )

    // restore label prefix when saving changes
    const handleKeyedContent = useCallback(
        (changedElements: Partial<TextContentElement>[]) => {
            if (isKeyedText && changedElements.length > 0) {
                changedElements[0].text = `${keyedText}${changedElements[0].text}`
            }
        },
        [isKeyedText, keyedText]
    )

    return {
        keyedText,
        keyedTextValue,
        isKeyedText,
        removeKeyedText,
        handleKeyedContent,
    }
}

/**
 * Custom hook to manage the state and interactions for a plain text content item
 * using CKEditor within the workspace. It handles editor activation, optimistic updates,
 * and synchronization with the workspace tree state.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item data, potentially incomplete.
 * @returns An object containing state, handlers, and derived values for the plain text editor.
 */
export const usePlainTextController = (
    contentItem: Partial<TextContentElement>
) => {
    const chatUser = useChatProfileStore((state) => state.chatUser)
    const textElement = useMemo(() => nullOrUndefinedResolver(contentItem), [
        contentItem,
    ])
    const [unsavedData, setUnsavedData] = useState<
        string | Record<string, string>
    >(textElement.text)
    const formattedContentId = useMemo(
        () => `plain-text-${textElement.id || 'default'}`,
        [textElement.id]
    )
    const initialSuggestions = useMemo(
        () => extractSuggestions([textElement]),
        [textElement.id, textElement.changeType]
    )

    const {
        isContentTeamFromStudioPage,
        isInProject,
        setContentItemViewNodes,
        setTextContentElementToAdd,
        setIsEditingPlainText,
    } = useShallowSelector((s) => ({
        isContentTeamFromStudioPage: s.isContentTeamFromStudioPage,
        isInProject: s.selectedProjectElementNode?.isInProject,
        setContentItemViewNodes: s.actions.setContentItemViewNodes,
        setTextContentElementToAdd: s.actions.setTextContentElementToAdd,
        setIsEditingPlainText: s.actions.setIsEditingPlainText,
    }))

    const { handlePromoteDemoteShortcutKey, register } = usePromoteDemote()

    const {
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
    } = usePlainTextContentMutation()

    let initialData = useMemo(() => elementToEditorData(textElement), [
        textElement,
    ])
    const {
        keyedText,
        keyedTextValue,
        isKeyedText,
        removeKeyedText,
        handleKeyedContent,
    } = useKeyedText(contentItem, isContentTeamFromStudioPage)

    if (isKeyedText) {
        initialData = removeKeyedText(initialData)
    }

    const ckeToggle = useCKEditorToggle(formattedContentId)
    const shouldRenderContentEditor = useMemo(
        () =>
            ckeToggle.active ||
            (Boolean(unsavedData) &&
                unsavedData.toString() !== textElement.text),
        [ckeToggle.active, unsavedData]
    )

    const { editorFocusRef, unFocusEditor } = useContentEditorFocus({
        contentItemId: contentItem.id,
        activate: ckeToggle.activate,
    })

    const { mutateContentElements } = useContentMutationService()
    const { executeWithOptimistic, registerEditorRef } = useTransactionManager()
    const { updateTreeViewNodes } = useTreeMutations()
    const {
        nodeToAdd,
        contentItemViewNodes,
        previousContentItemViewNodes,
    } = useContentItemViewNode()

    const handleOnBlur = useCallback<ClassicEditorHandler>(
        async (_, editor) => {
            const unsavedDataString = unsavedData.toString()
            const hasNoChanges = unsavedDataString === textElement.text

            // no changes case - restore previous state and toggle off editor
            // Batch updates for no changes case
            if (hasNoChanges) {
                if (nodeToAdd?.id && previousContentItemViewNodes) {
                    // Use a single update operation
                    const updates = {
                        unsavedData: textElement.text,
                        textContentElement: null,
                        contentItemViewNodes: previousContentItemViewNodes,
                    }

                    // Implement a batchUpdate function to reduce renders
                    batchUpdate(() => {
                        setUnsavedData(updates.unsavedData)
                        setTextContentElementToAdd(updates.textContentElement)
                        setContentItemViewNodes(updates.contentItemViewNodes)
                    })
                }

                setIsEditingPlainText(false)
                return ckeToggle.deactivate()
            }

            // there is a node to add
            if (nodeToAdd && nodeToAdd.id) {
                unFocusEditor()
                return await handleAddContentExecuteOptimisticWithFallback(
                    unsavedDataString,
                    textElement
                )
            }

            const htmlContent = editor.getData()
            let changedElements = processContent(htmlContent, chatUser?.id)
            handleKeyedContent?.(changedElements)

            executeWithOptimistic({
                fallbackState: initialData,
                asyncAction: () =>
                    mutateContentElements(textElement.id, changedElements),
                successCbFn: async () => {
                    await handleContentMutationCompleted(
                        textElement,
                        nodeToAdd?.type === 'Article'
                    )
                },
            })
            setUnsavedData(isKeyedText ? textElement.text : unsavedDataString)
            setIsEditingPlainText(false)
            ckeToggle.deactivate()
        },
        [
            unsavedData,
            textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            ckeToggle,
            chatUser?.id,
            contentItemViewNodes,
            initialData,
            mutateContentElements,
            executeWithOptimistic,
            updateTreeViewNodes,
            setContentItemViewNodes,
            setTextContentElementToAdd,
            handleKeyedContent,
            isKeyedText,
            unFocusEditor,
            handleAddContentExecuteOptimisticWithFallback,
            processContent,
            getUnresolvedChanges,
            batchUpdate,
            setIsEditingPlainText,
        ]
    )

    const debouncedOnChange = useMemo(
        () =>
            withDebounce<ClassicEditorHandler>((_, editor) => {
                const hasInitialSuggestions = initialSuggestions.length > 0

                const textChanges = getUnresolvedChanges(
                    editor.getData(),
                    textElement.text,
                    hasInitialSuggestions
                )
                setUnsavedData(textChanges)
            }, 300),
        [textElement.text, initialSuggestions]
    )

    // side-effect that will prevent memory leaks from unloading CKEditor
    useEffect(() => {
        return () => debouncedOnChange.cancel()
    }, [debouncedOnChange])

    const handleOnFocusCapture = useCallback(() => {
        setIsEditingPlainText(true)
        register(contentItem, isInProject, contentItemViewNodes)
        ckeToggle.activate()
    }, [
        setIsEditingPlainText,
        register,
        contentItem,
        isInProject,
        contentItemViewNodes,
    ])

    const handleOnKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            handlePromoteDemoteShortcutKey(event)
        },
        [handlePromoteDemoteShortcutKey]
    )

    const handlers: PlainTextHandlers = useMemo(
        () => ({
            onReady: (editor) => {
                editorFocusRef.current = editor
                // register the CKEditor instance to use in Transaction Manager (optimistic update)
                registerEditorRef(editor)
            },
            onBlur: handleOnBlur,
            onChange: debouncedOnChange,
            onFocusCapture: handleOnFocusCapture,
            onKeyDown: handleOnKeyDown,
        }),
        [
            handleOnBlur,
            debouncedOnChange,
            registerEditorRef,
            handleOnFocusCapture,
            handleOnKeyDown,
        ]
    )

    return {
        text: textElement.text,
        formattedContentId,
        initialData,
        initialSuggestions,
        ckeToggle,
        shouldRenderContentEditor,
        isKeyedText,
        keyedText,
        keyedTextValue,
        handlers,
        setIsEditingPlainText,
    }
}
